import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, CheckCircle, FileEdit, Inbox, Mail, Image, ArrowRight, Plus } from "lucide-react";
import api from "@/lib/api";
import { formatDate } from "@/lib/util";
import { PageTitle, Card } from "@/components/admin/ui";

const STATUS_COLORS = { NEW: "bg-blue-100 text-blue-700", CONTACTED: "bg-amber-100 text-amber-700", CLOSED: "bg-green-100 text-green-700" };

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);

  const cards = stats ? [
    { label: "Total Blog Posts", value: stats.total_posts, Icon: FileText, to: "/admin/blog" },
    { label: "Published Posts", value: stats.published, Icon: CheckCircle, to: "/admin/blog" },
    { label: "Draft Posts", value: stats.drafts, Icon: FileEdit, to: "/admin/blog" },
    { label: "New Enquiries", value: stats.new_enquiries, Icon: Inbox, to: "/admin/enquiries", highlight: true },
    { label: "Total Enquiries", value: stats.total_enquiries, Icon: Mail, to: "/admin/enquiries" },
    { label: "Media Files", value: stats.media_files, Icon: Image, to: "/admin/media" },
  ] : [];

  return (
    <div data-testid="admin-dashboard">
      <PageTitle title="Dashboard" subtitle="Overview of your website content and enquiries">
        <Link to="/admin/blog/new" className="bg-navy text-ivory px-4 py-2.5 rounded-sm text-sm inline-flex items-center gap-2 hover:bg-navy-light"><Plus size={16} /> New Article</Link>
      </PageTitle>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} className={`bg-white border rounded-lg p-5 hover:shadow-md transition-shadow ${c.highlight && c.value > 0 ? "border-brass" : "border-slate-200"}`} data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between mb-3">
              <span className={`w-10 h-10 rounded-sm flex items-center justify-center ${c.highlight && c.value > 0 ? "bg-brass text-navy" : "bg-navy/5 text-navy"}`}><c.Icon size={18} /></span>
              {c.highlight && c.value > 0 && <span className="w-2.5 h-2.5 bg-brass rounded-full animate-pulse" />}
            </div>
            <p className="font-serif text-3xl text-navy">{c.value}</p>
            <p className="text-slate-500 text-xs mt-1 uppercase tracking-wide">{c.label}</p>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Recent Enquiries">
          {stats?.recent_enquiries?.length ? (
            <div className="space-y-3">
              {stats.recent_enquiries.map((e) => (
                <div key={e.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="min-w-0">
                    <p className="text-navy font-medium text-sm truncate">{e.name}</p>
                    <p className="text-slate-400 text-xs truncate">{e.subject}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full uppercase tracking-wide shrink-0 ${STATUS_COLORS[e.status] || ""}`}>{e.status}</span>
                </div>
              ))}
              <Link to="/admin/enquiries" className="text-brass text-sm font-medium inline-flex items-center gap-1 mt-2">View all <ArrowRight size={14} /></Link>
            </div>
          ) : <p className="text-slate-400 text-sm">No enquiries received.</p>}
        </Card>

        <Card title="Recent Blog Posts">
          {stats?.recent_posts?.length ? (
            <div className="space-y-3">
              {stats.recent_posts.map((p) => (
                <div key={p.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="min-w-0">
                    <p className="text-navy font-medium text-sm truncate">{p.title}</p>
                    <p className="text-slate-400 text-xs">{formatDate(p.updated_at)}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full uppercase tracking-wide shrink-0 ${p.status === "published" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{p.status}</span>
                </div>
              ))}
              <Link to="/admin/blog" className="text-brass text-sm font-medium inline-flex items-center gap-1 mt-2">View all <ArrowRight size={14} /></Link>
            </div>
          ) : <p className="text-slate-400 text-sm">No articles published yet.</p>}
        </Card>
      </div>
    </div>
  );
}
